export interface Size {
    width: number;
    height: number;
}

export interface Point {
    x: number;
    y: number;
}

export interface Rect extends Point, Size {}

export interface PositionedRect extends Rect {
    left: number;
    top: number;
}

export interface ScaleResult {
    scaledRect: Rect;
    offsetX?: number;
    offsetY?: number;
    scaleX: number;
    scaleY: number;
}

export const getScaleFactors = ({
    source,
    target = { width: 1, height: 1 }
}: {
    source: Size;
    target?: Size;
}) => {
    // target scale defaults to 1x1
    const scaleX = target.width / source.width;
    const scaleY = target.height / source.height;
    return { scaleX, scaleY };
};

export const getScaledOffset = ({
    source,
    rect,
    scaleX,
    scaleY
}: {
    source: PositionedRect;
    rect: PositionedRect;
    scaleX: number;
    scaleY: number;
}) => {
    const offsetX = (rect.left - source.left) * scaleX;
    const offsetY = (rect.top - source.top) * scaleY;

    return { offsetX, offsetY };
};

export const getScaledRect = ({
    rect,
    scaleX,
    scaleY
}: {
    rect: Rect;
    scaleX: number;
    scaleY: number;
}): Rect => {
    return {
        x: rect.x * scaleX,
        y: rect.y * scaleY,
        width: rect.width * scaleX,
        height: rect.height * scaleY
    };
};

export const scaleRect = ({
    source,
    target,
    rect,
    offset = false
}: {
    source: Size | PositionedRect;
    target: Size;
    rect: Rect | PositionedRect;
    offset?: boolean;
}): ScaleResult => {
    // Calculate scale and offset

    const { scaleX, scaleY } = getScaleFactors({ source, target });

    const scaledRect = getScaledRect({ rect, scaleX, scaleY });

    const result: ScaleResult = { scaledRect, scaleX, scaleY };

    if (offset) {
        const { offsetX, offsetY } = getScaledOffset({
            source: source as PositionedRect,
            rect: rect as PositionedRect,
            scaleX,
            scaleY
        });

        result.offsetX = offsetX;
        result.offsetY = offsetY;
    }

    return result;
};
